import moment from "moment";

import { ClickableTextItem } from "@/Common/Api/ClickableText/Dto/ClickableTextItem";
import { Email } from "@/Common/Api/Contacts/Dto/Email";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { ILoginPinFrequencyModel } from "@/Common/Api/Employee/ILoginFrequencyModel";
import { OrphanLabForwardDTO } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import { LicenseDto } from "@/Common/Api/Labs/Dto/Responses/LicenseDto";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { LicenceOrganisationRule } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationRule";
import { Service } from "@/Common/Api/Services/Dto/Responses/Service";
import { ServiceComboDefinition } from "@/Common/Api/Services/Dto/Responses/ServiceDefinition";
import { ValidationRule } from "@/Common/Api/Validation/Dto/Responses/ValidationRule";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { IAllowlistModel } from "@/Common/Models/Administration/IAllowlistModel";
import { DrugInteractionAlertsFilterOption } from "@/Common/Models/Employee/Enums/DrugInteractionAlertsFilterOption";
import { EmployeeTitle } from "@/Common/Models/Employee/Enums/EmployeeTitle";
import { EmployeeType } from "@/Common/Models/Employee/Enums/EmployeeType";
import { PrescriptionPrintType } from "@/Common/Models/Employee/Enums/Settings";
import { Guid } from "@/Common/Models/Guid";
import { ServiceModel } from "@/Common/Models/Services/ServiceModel";
import { ProfilePicture as MyleModelsCommonProfilePicture } from "@/Common/Models/UI/ProfilePicture";
import { EmployeeGroup } from "./EmployeeGroup";
import { License } from "./License";

/**
* @description Used to be EmployeeComposite
*/
class Employee {
    public ContactFacet_id: Guid;
    public Contact_id: Guid;
    public Type: EmployeeType;

    // EmployeeTable Data
    public AssociatedSites: Guid[];
    public ActiveSiteId: Guid;
    public ActiveDepartmentId: string;

    public IsInVacation: boolean;
    public FromVacationDate: Date;
    public ToVacationDate: Date;
    public VacationNote: string;

    public IsSharingLabs: boolean;
    public CeLoginRamq: string; // RAMQ Services UserName
    public LabDesactivationDate: string;
    public ProfessionalNumber: string;
    public StatusLookupId: string;
    public RemunerationTypeLookupId: string;
    public DescriptionProfessional: string;
    public Biography: string;
    public IsSendNotificationByEmail: boolean;
    public DispensationNumber: string;
    public AdministrativeAccountNumber: string;

    public OnlineFirstName: string;
    public OnlineLastName: string;
    public HubFirstName: string;
    public HubLastName: string;

    public AuthenticatorEnabled: boolean;
    public AllowPatientsToInitiatePortalMessages: boolean;
    // --------------------

    public HasPatientNoteAccess: boolean;

    // InvidivualTable Data
    // Individual: Individual;
    public PrefixLookupId: string;
    public FirstName: string;
    public LastName: string;
    public IsInactive: boolean; // From IndividualInactivable
    public GenderLookupId: string;
    public MiddleName: string;
    public SecondName: string;
    public SuffixLookupId: string;
    public NAM: string;
    public MaidenName: string;
    public PreferredOfficialLanguageLookupId: string;
    // ---------------------

    // RAMQ Info
    public GroupNumber: string;
    // ---------------

    // Refactored, ToReConnect
    public ServicesIDsOffered: Service[]; // Array of ServiceID
    public CombosOffered: ServiceComboDefinition[];
    public Permissions: string[] = []; // Array of PermissionId granted at individual level
    public RolePermissions: string[] = []; // Array of PermissionId granted by roles
    public Roles: string[] = []; // Array of RoleId
    public Licences: License[] = []; // Commented All UseLessFields inside Licence
    public FirstLicenceNumber: string; // Licence.FirstOrDefaults().LicenceNumber...
    public Phones: PhoneNumber[] = [];

    public Emails: Email[] = [];
    public Groups: EmployeeGroup[];

    public MessagesSharing: string[] = [];
    public TasksSharing: string[] = [];
    public ExternalCommunicationsSharing: string[] = [];
    public PortalCommunicationsSharing: string[] = [];

    public OrphanLabForwardDTOs: OrphanLabForwardDTO[] = [];

    public TextList: ClickableTextItem[] = [];

    // -----------------------
    // Login  and PasswordSecurity and UserTable Info
    public LastConnection: Date;
    public DateActivated: Date;

    public EndValidity: Date;
    public StartValidity: Date;
    public CreationDate: Date;
    public ValidityPeriodPasswordDays: number = 365;
    public TimeoutDelay: number = 300;
    public Password: string;
    public Language: string;
    public UserName: string;
    public IsTemporaryPassword: boolean;
    public HasPinSet: boolean;
    public User_id: string;
    // --------------------------
    // Contact Info
    // --------------------------
    // Only Current User
    public ProfilePicture: MyleModelsCommonProfilePicture;
    public UsefulLinks: string[];
    public DefaultRefill: number;
    public SignFileName: string;
    public PrintingSetting: boolean; // true: landscape | false: portrait  --printer settings (yeh, wtf?..)
    public LargeLabelPrinting: boolean; // true: 3cm | false: 2cm
    public IsRegularPrinter: boolean = true; // true: regular | false: thermal
    public PrescriptionPrintType: PrescriptionPrintType;
    public IsRegularRDVConfirmPrinter: boolean = true; // true: regular | false: thermal
    public AllowedFormTags: string[]; // * separated tags... yeh
    public PharmacotherapeuticAssistantDisable: boolean;
    public IsProblemVisibility: boolean;
    public ThumbprintKeyDsq: string;
    public NUI: string;
    public LicenceDSQ: string;
    public LicenceRoot: string;
    public LastNameDSQ: string;
    public FirstNameDSQ: string;
    public CertificatePin: string;
    public SignatureByteArray: string;
    public PreferedFaxAccountNumberId: number;
    public LoginPinFrequency: ILoginPinFrequencyModel;
    public ShouldAttachMedicalSummaryWhenCreatingConsultationRequest: boolean;
    public ShouldAttachMedicationSummaryWhenCreatingConsultationRequest: boolean;
    public DrugInteractionAlertsFilterOption: DrugInteractionAlertsFilterOption;

    // ToComputeAfterCacheLoad
    public PrefixLookup: FlatLookupView;
    public ServicesOffered: ServiceModel[]; // contains only service models of services that are not inactive for an employee

    public ShouldShowAndCalculateMedicationDispensationQuantities?: boolean;
    public ShowDiagnosisCode: boolean;

    public ShowPopupForNewAlertsAndSpecialNeeds: boolean;
    public ShowAlertsAndSpecialNeedsInPrintedSummary: boolean;

    public ShowTelemedWindowInMylePopup: boolean;

    public DisableTimers: boolean;
    public University: string;
    public SpecialtyLookupId: string;
    public LicenseOrganizationSpecializationId: string;

    // Facturation
    public RamqNumber: string;
    public BillingProvider: string;
    public BillingAgencyNumber: string;
    public ExternalBillingPaymentModeLookupId: string;

    // BookingHub feature flag
    public BookingHubEnabled: boolean;

    // Public API feature flag
    public VisibleOnline: boolean;

    // long-term medication feature (EnableLongTermMedication)
    public LongTermMedicationDurationInDays?: number;
    public LongTermMedicationDefaultRefillNumber?: number;

    public MedicalNoteAllowlistChanges: IAllowlistModel = null;
    public MedicalNoteAllowlist: IAllowlistModel = null;

    constructor(fields?: Partial<Employee>) {
        Object.assign(this, fields);
    }

    public static getIssuingOrgRule(employee: Employee): LicenceOrganisationRule {
        if (!employee)
            return null;

        const ruleId = employee.Licences?.firstOrDefault()?.IssuingLicenseOrganizationRuleId;

        return cacheStore.AllLicenseOrganizationRulesById[ruleId] ?? null; // avoid undefined
    }

    public static setIssuingOrgRule(employee: Employee, licenseOrganizationRuleId: string): void {
        if (!employee.Licences?.any()) {
            employee.Licences = [
                new License({ LicenceNumber: "" }) // The database doesn't allow null LicenseNumber
            ];
        }
        employee.Licences[0].IssuingLicenseOrganizationRuleId = licenseOrganizationRuleId;
    }

    public static getTitle(employee: Employee): EmployeeTitle {
        return Employee.getIssuingOrgRule(employee)?.Title;
    }

    public static isLastNameValid(e: Employee): boolean {
        if (e == null) return false;
        return !IsNullOrWhiteSpace(e.LastName);
    }

    public static isFirstNameValid(e: Employee): boolean {
        if (e == null) return false;
        return !IsNullOrWhiteSpace(e.FirstName);
    }

    public static isGenderValid(e: Employee): boolean {
        if (e == null) return false;
        return !IsNullOrWhiteSpace(e.GenderLookupId);
    }

    public static isTitleValid(e: Employee): boolean {
        if (e == null) return false;
        return !!this.getIssuingOrgRule(e);
    }

    public static isTimeoutDelayValid(e: Employee): boolean {
        if (e == null) return false;

        return e.Type === EmployeeType.Api // API users do not have timeout delays since they do not log in
            || e.TimeoutDelay >= 2; // The timeout delay should never be under 2 minutes as it's used to generate the token to authenticate
    }

    public static isPhoneValid(e: Employee): boolean {
        if (e == null) return false;
        let validationHelper = new ValidationHelper();

        let valid = (e.Phones == null);
        if (!valid) {
            if (e.Phones != null && e.Phones.length > 0) {
                let phoneCountryLookupId = e.Phones[0].CountryLookupId;
                let validationRule: ValidationRule = (phoneCountryLookupId != null) ? validationHelper.getPhoneNumberValidationRule(phoneCountryLookupId) : null;
                let validationRegex = validationRule ? validationRule.ValidationRegexPattern : "";
                let regexp = new RegExp(validationRegex, "i");

                // valid = regexp.test(e.Phones[0].Number);
                valid = ValidationHelper.isExactMatch(e.Phones[0].Number, regexp);
            }
        }

        if (e == null || e.Phones == null || e.Phones.length == 0)
            return true;
        else {
            if (e.Phones[0].Number) {
                return e.Phones[0].Number.length == 0 || valid;
            }
            else return true;
        }
    }

    public static isEmailValid(e: Employee): boolean {
        if (e == null) return false;

        let valid = (e.Emails == null);
        if (!valid) {
            if (e.Emails != null && e.Emails.length > 0)
                valid = ValidationHelper.isExactMatch(e.Emails[0].Address, ValidationHelper.emailRegex);
        }

        if (e == null || e.Emails == null || e.Emails.length == 0)
            return true;
        else
            return e.Emails[0].Address.length == 0 || valid;
    }

    public static isStartDateValid(e: Employee): boolean {
        if (e == null) return true;

        if (e.StartValidity != null) {
            return e.EndValidity == null || moment(e.EndValidity) > moment(e.StartValidity);
        }
        return true;
    }

    public static isEndDateValid(e: Employee): boolean {
        if (e == null) return true;

        if (e.EndValidity != null) {
            return e.StartValidity == null || moment(e.EndValidity) > moment(e.StartValidity);
        }
        return true;
    }

    public static isUserNameValid(e: Employee): boolean {
        if (e == null) return false;
        return !IsNullOrWhiteSpace(e.UserName);
    }

    public static isTemporaryPasswordValid(e: Employee): boolean {
        if (e == null) return false;
        return !IsNullOrWhiteSpace(e.Password);
    }

    public static isSitesValid(e: Employee): boolean {
        if (e == null) return false;
        return e.AssociatedSites != null && e.AssociatedSites.length > 0;
    }

    public static isPermissionValid(e: Employee): boolean {
        if (e == null) return false;
        return e.Permissions != null && e.Permissions.length > 0;
    }

    public static getFullNameWithPrefix(employeeContactId: string): string {
        if (IsNullOrWhiteSpace(employeeContactId))
            return "";

        let employee = cacheStore.GetEmployeeByContactIdOrEmpty(employeeContactId);
        if (employee == null)
            return employeeContactId;

        let employeeTitle = cacheStore.GetLocalizedValueByLookupId(employee.PrefixLookupId);

        return employeeTitle + " " + employee.LastName + ", " + employee.FirstName;
    }

    public static getFullNameWithPrefixContactFacetForEmployee(employee: Employee): string {
        if (!employee)
            return "";

        let employeeTitle = cacheStore.GetLocalizedValueByLookupId(employee.PrefixLookupId);
        const employeeFullName = `${employee.LastName}, ${employee.FirstName}`;
        return IsNullOrWhiteSpace(employeeTitle)
            ? `${employeeFullName}`
            : `${employeeTitle} ${employeeFullName}`;
    }

    public static getFullNameWithPrefixContactFacet(employeeContacFacettId: string): string {
        if (IsNullOrWhiteSpace(employeeContacFacettId))
            return "";

        let employee = cacheStore.GetEmployeeByContactFacetId(employeeContacFacettId);

        if (employee == null)
            return employeeContacFacettId;

        let employeeTitle = cacheStore.GetLocalizedValueByLookupId(employee.PrefixLookupId);

        return `${employeeTitle} ${employee.LastName}, ${employee.FirstName}`;
    }

    public static shouldHaveSupervisorBasedOnTitle(e: Employee): boolean {
        const title = this.getIssuingOrgRule(e)?.Title;
        switch (title) {
            case EmployeeTitle.Student:
            case EmployeeTitle.Extern:
            case EmployeeTitle.Resident:
            case EmployeeTitle.Intern:
                return true;

            default:
                return false;
        }
    }

    /// <summary>Format license number. *This function is replicated in the backend in LicenseHelper.cs</summary>
    public static getFormattedLicence(employee: Employee, usePractitionerNumberWhenRequired: boolean): string {
        if (!employee)
            return "";

        const license = employee?.Licences?.firstOrDefault();
        const licenceOrganisationRule = Employee.getLicenceOrganisationRule(employee);

        if (licenceOrganisationRule == null)
            return license?.LicenceNumber ?? "";

        if (licenceOrganisationRule?.StateCode == "BC" && licenceOrganisationRule?.CountryCode == "CA" && usePractitionerNumberWhenRequired)
            return `MSP: ${employee.ProfessionalNumber ?? cacheStore.GetString("NotApplicableLabel")}}`;

        // additional suffix for IPS Title
        const suffix = Employee.GetSuffix(licenceOrganisationRule);
        const licenseNumber = license?.LicenceNumber?.replace(`-${licenceOrganisationRule.LicenceOrganisationCode}`, "");

        return `${licenceOrganisationRule.Acronym}: ${licenseNumber} ${suffix}`
            ?.trim();
    }

    public static getLicenceOrganisationRule(employee: Employee): LicenceOrganisationRule {
        const license = employee?.Licences?.firstOrDefault();
        const licenseOrganizationRule = cacheStore.AllLicenseOrganizationRulesById[license?.IssuingLicenseOrganizationRuleId] ?? null;
        return licenseOrganizationRule;
    }

    private static GetSuffix(licenseOrganizationRule: LicenceOrganisationRule): string {
        const countryCode = licenseOrganizationRule?.CountryCode;
        const stateCode = licenseOrganizationRule?.StateCode;
        const organizationCode = licenseOrganizationRule?.OrganisationCode;
        const title = licenseOrganizationRule?.Title;

        if (countryCode == "CA" && title == EmployeeTitle.NursePractitioner
            && (
                (stateCode == "QC" && organizationCode == "OIIQ")
                || (stateCode == "BC" && organizationCode == "BCCNM")
            )
        ) {
            return cacheStore.GetString("NurseSpecializedLabel");
        }

        return "";
    }

    public static getLicenceSuffix(emp: Employee): string {
        return License.getSuffix(emp?.Licences?.firstOrDefault());
    }

    public static getLicenseDtos(employee: Employee, usePractitionerNumberWhenRequired: boolean = true, getFirstOnly: boolean = false): LicenseDto[] {
        if (!employee)
            return [];

        const labDeactivationDate = DateHelper.parseOptional(employee.LabDesactivationDate);
        const hasExtraNumber = employee.IsInactive && labDeactivationDate && labDeactivationDate.isBefore(moment());

        const licenseDtos: LicenseDto[] = [];

        for (let license of employee.Licences) {
            const licenseOrganization = License.getIssuingOrgRule(license);
            const endDate = hasExtraNumber ? (DateHelper.formatDate(employee.LabDesactivationDate, "YYYYMMDD") || "") : "";
            let shouldApplySuffix = cacheStore.ArlMyleDatabaseLocation?.StateCode !== "QC";
            if (licenseOrganization?.LicenceOrganisationCode == "OTH" || !licenseOrganization?.OrganisationCode)
                shouldApplySuffix = false;

            // BC uses the professionnal number for lab reception
            if (usePractitionerNumberWhenRequired && licenseOrganization?.StateCode == "BC" && licenseOrganization?.CountryCode == "CA") {
                licenseDtos.push(new LicenseDto({
                    LicenseNumber: employee.ProfessionalNumber,
                    Suffix: ".MSP.BC.CA",
                    EndDate: endDate,
                    ShouldApplySuffix: shouldApplySuffix,
                    EmployeeContactFacetId: employee.ContactFacet_id
                }));
            }
            else {
                licenseDtos.push(new LicenseDto({
                    LicenseNumber: employee.FirstLicenceNumber ?? "",
                    Suffix: this.getLicenceSuffix(employee),
                    EndDate: endDate,
                    ShouldApplySuffix: shouldApplySuffix,
                    EmployeeContactFacetId: employee.ContactFacet_id
                }));
            }

            if (getFirstOnly && licenseDtos.length > 0)
                return licenseDtos;
        }

        return licenseDtos;
    }

    public static getLicenseDto(employee: Employee, usePractitionerNumberWhenRequired: boolean = true): LicenseDto {
        return this.getLicenseDtos(employee, usePractitionerNumberWhenRequired, true).firstOrDefault();
    }

    public static getFullNameWithPrefixNameSuffixTitle(employeeContactFacetId: string, ignoreLicenceNumber: boolean = false): string {
        const employee = cacheStore.GetEmployeeByContactFacetId(employeeContactFacetId);
        if (!employee)
            return "";

        return this.getFullNameWithPrefixNameSuffixTitleForEmployee(employee, ignoreLicenceNumber);
    }

    public static getFullNameWithPrefixNameSuffixTitleForEmployee(employee: Employee, ignoreLicenceNumber: boolean = false): string {
        if (!employee)
            return "";

        const nameWithPrefixString = this.getFullNameWithPrefixContactFacetForEmployee(employee);

        const suffixString = !IsNullOrWhiteSpace(employee.SuffixLookupId)
            ? " " + cacheStore.GetLocalizedValueByLookupId(employee.SuffixLookupId)
            : "";

        const licenseNumber = employee.Licences[0]?.LicenceNumber ?? "";
        const licenseString = licenseNumber ? " (" + licenseNumber + ")" : "";

        const issuingOrgRule = this.getIssuingOrgRule(employee);
        const issuingOrgRuleTitleString = issuingOrgRule ? " - " + LicenceOrganisationRule.getTitleName(issuingOrgRule) : "";

        return nameWithPrefixString + suffixString + (!ignoreLicenceNumber ? licenseString : "") + issuingOrgRuleTitleString;
    }

    public static getCleanLicenseNumber(employee: Employee): string {
        // get the employee licence number without any special character or letter.
        return employee.FirstLicenceNumber.replace(/\D/g, "");
    }
}

export { Employee };
